import { jsxs, jsx } from "react/jsx-runtime";
import { styled } from "@mui/material/styles";
import { ArrowRightIcon } from "../../../icons/ArrowRightIcon.js";
import { SuccessCheckCircleIcon } from "../../../icons/SuccessCheckCircleIcon.js";
import { formatMoney, escapeUriPath } from "../../../utils/stringUtils.js";
import { ButtonLoadingEllipsis } from "../Loading/index.js";
import { MOBILE_SCREEN_SIZE } from "../../../utils/constants/Constants.js";
import { Button } from "../../../Components/Button/Button.js";
const BrandCardContainer = styled("div")(() => ({
  border: "solid 1px #dcdddc",
  width: "100%",
  height: "100%",
  borderRadius: 12,
  // matches product card padding
  padding: "16px 16px 16px 22px",
  paddingTop: 58,
  cursor: "pointer",
  boxSizing: "border-box",
  transition: "background-color 0.4s ease-in-out",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0,
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    padding: 10,
    paddingTop: 52,
    width: "100%"
  }
}));
const BrandLogoContainer = styled("div")(() => ({
  width: "calc(100% - 24px)",
  maxWidth: "190px",
  aspectRatio: "1/1",
  margin: "0 auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center"
}));
const BrandLogoImg = styled("img")(({ theme }) => ({
  width: "100%",
  maxWidth: 149,
  aspectRatio: "1/1",
  backgroundColor: theme.vars.palette.common.white,
  objectFit: "contain",
  borderRadius: 999
}));
const BrandName = styled("h2")(({ theme }) => ({
  fontSize: 22,
  fontWeight: 600,
  marginBottom: 16.7,
  marginTop: 20,
  color: theme.vars.palette.text.primary,
  overflow: "hidden",
  width: "80%",
  textAlign: "center",
  textDecoration: "none",
  letterSpacing: 0.36,
  WebkitLineClamp: 1,
  lineHeight: 1.5,
  height: `1.5em`,
  // line height * 2
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  wordBreak: "break-word",
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: 16
  }
}));
const BrandCardStatusMessage = styled("div")(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  marginTop: 29.3,
  color: theme.vars.palette.text.primary,
  display: "flex",
  alignItems: "center",
  textAlign: "center",
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: 12
  },
  ".arrow": {
    width: 13,
    height: 13,
    objectFit: "contain",
    marginLeft: 10,
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      width: 10,
      height: 10
    }
  }
}));
const BrandCardButton = styled(Button)(() => ({
  height: 40,
  fontSize: 17,
  fontWeight: 600,
  minWidth: 108,
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    height: 30,
    fontSize: 12,
    minWidth: 88
  }
}));
const getBrandActivityPath = (brand) => `/${escapeUriPath(brand.name)}/b/${brand.brandId}/branddashboard/rewards/activity`;
const getBrandHomePath = (brand) => `/${escapeUriPath(brand.name)}/b/${brand.brandId}/home`;
const BrandCardJoinButton = (props) => {
  var _a;
  if (props.joined) {
    return /* @__PURE__ */ jsx(BrandCardButton, { variant: "outline", color: "primary", children: formatMoney(((_a = props.brand) == null ? void 0 : _a.rewardAmount) ?? 0) });
  }
  if (props.joinedState === "not_join") {
    return /* @__PURE__ */ jsx(Button, { variant: "solid", color: "tertiary", onClick: props.onJoin, children: "Join" });
  }
  if (props.joinedState === "joining") {
    return /* @__PURE__ */ jsxs(Button, { variant: "solid", color: "tertiary", children: [
      "Joining",
      /* @__PURE__ */ jsx(ButtonLoadingEllipsis, { baseFontSize: 17, blue: true })
    ] });
  }
  return /* @__PURE__ */ jsxs(BrandCardButton, { children: [
    /* @__PURE__ */ jsx(SuccessCheckCircleIcon, { className: "button_img" }),
    "Member"
  ] });
};
const BrandCardJoinMessage = (props) => {
  const { brand, LinkComponent } = props;
  const viewActivityPath = getBrandActivityPath(brand);
  const homePath = getBrandHomePath(brand);
  if (props.joinedState === "joined" && props.reward) {
    return /* @__PURE__ */ jsx(BrandCardStatusMessage, { children: `You earned ${formatMoney(props.reward)} by joining this club` });
  }
  return /* @__PURE__ */ jsx(
    LinkComponent,
    {
      to: props.joined ? viewActivityPath : homePath,
      style: {
        textDecoration: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%"
      },
      children: /* @__PURE__ */ jsxs(BrandCardStatusMessage, { children: [
        /* @__PURE__ */ jsx("span", { children: props.joined ? "View activity" : `Visit ${brand.name}` }),
        /* @__PURE__ */ jsx(ArrowRightIcon, { className: "arrow", color: "primary" })
      ] })
    }
  );
};
const ProductBrandCard = ({ brand, joined, joinedState, reward, LinkComponent, ...props }) => {
  return /* @__PURE__ */ jsxs(BrandCardContainer, { children: [
    /* @__PURE__ */ jsxs(
      LinkComponent,
      {
        to: getBrandHomePath(brand),
        style: {
          textDecoration: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%"
        },
        children: [
          /* @__PURE__ */ jsx(BrandLogoContainer, { children: /* @__PURE__ */ jsx(BrandLogoImg, { alt: "logo", src: brand.imageUrl }) }),
          /* @__PURE__ */ jsx(BrandName, { children: brand.name }),
          /* @__PURE__ */ jsx(BrandCardJoinButton, { brand, joined, onJoin: props.onJoin, joinedState })
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      BrandCardJoinMessage,
      {
        joinedState,
        reward,
        joined,
        brand,
        LinkComponent
      }
    )
  ] });
};
export {
  ProductBrandCard as default
};
//# sourceMappingURL=ProductBrandCard.js.map
